/**
 * GIVING THE RETENTION BACK.
 *
 * Retention is the slice OVAL keeps off every running account bill — 5% on
 * most works contracts — and holds as security that the work will not fall
 * apart the moment the contractor leaves site. It is the contractor's money.
 * OVAL is holding it, not earning it.
 *
 * Until 0154 there was no way to give it back. The statutory pack could report
 * how much was held; nothing in the system could release a rupee of it. It
 * came back the way everything used to come back here — a letter, a cheque and
 * somebody's memory of what the contract said.
 *
 * WHAT THE BOARD DECIDED, 20-09-2026
 *
 * Any amount, any time. NOT the two fixed tranches clause 150 of the standard
 * conditions describes (half on completion, half when the defect liability
 * period ends). That was my recommendation and it was not taken, and the
 * instruction is the right one for this business: OVAL settles retention
 * against snag lists, part completions and negotiations that no schedule
 * anticipates.
 *
 * So the system enforces exactly one thing about the amount — ARITHMETIC. You
 * cannot release more than is held, less whatever has already gone back or is
 * in the chain on its way back. Everything else is judgement, and judgement is
 * recorded rather than constrained: the reason is compulsory, it is kept for
 * ever, and this screen prints the clause 150 figure beside the box so the man
 * typing knows what the contract would have given if he departs from it.
 *
 * WHY A RELEASE IN THE CHAIN STILL COUNTS AGAINST THE CEILING
 *
 * Two people, two screens, the same order, the same morning. If only APPROVED
 * releases counted, both could raise the last 7,600 and both would be within
 * the ceiling when they raised it. fn_retention_released counts anything not
 * rejected or cancelled, so the second one is refused while the first is still
 * with the Project Head. The money is spoken for the moment somebody asks for
 * it.
 *
 * THE CHAIN IS PROJECT HEAD THEN CMD
 *
 * Two stages, not the five a work order runs. Releasing retention is not a
 * procurement decision — nothing is being bought. It is Projects certifying
 * that the work stood up, and the CMD releasing the money.
 */
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Banknote, CalendarClock, CheckCircle2, FilterX,
  HandCoins, Landmark, Lock, Send, ShieldCheck, Trash2, Wallet, FileDown,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { dbMessage, useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, inr, rupees, localIso } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select,
  Stat, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { ApprovalChain } from '../components/ApprovalChain'

const n = (v: unknown) => Number(v ?? 0) || 0

/** Who may raise one. Mirrors rpc_retention_raise; the RPC is the enforcement. */
const MAY_RAISE = ['admin', 'accounts_spoc', 'project_head', 'procurement_head']
/** Who may record the money going out. Mirrors rpc_retention_record_payment. */
const MAY_PAY = ['admin', 'accounts_spoc']

interface HoldingRow {
  wo_id: string; site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null
  title: string | null; wo_status: string
  retention_held: number; retention_released: number; retention_paid_out: number
  retention_outstanding: number
  releases_in_chain: number; releases_awaiting_payment: number
  first_tranche: number; second_tranche: number
  work_certified_complete: boolean; dlp_expired: boolean
  dlp_ends_on: string | null; days_to_dlp_end: number | null
  closure_basis: string
}

interface ReleaseRow {
  id: string; wo_id: string; wo_title: string | null
  site_id: string; site_name: string | null
  vendor_id: string; vendor_name: string | null; vendor_pan: string | null
  amount: number; reason: string; remarks: string | null
  held_at_raising: number; released_before: number; tranche_guide: string | null
  held_now: number; released_now: number; unreleased_now: number
  status: string; current_step: number
  paid_on: string | null; mode: string | null; reference_no: string | null
  is_paid: boolean
  raised_by_name: string | null; creator_email: string | null
  approver_email: string | null; approval_date: string | null
  return_reason: string | null; rejection_reason: string | null
  date_created: string; created_at: string; created_by: string | null
  completion_date: string | null; dlp_ends_on: string | null
  attachment_count: number
}

/* ========================================================================== */
/*  THE REGISTER                                                              */
/* ========================================================================== */

const BLANK = { term: '', site: '', vendor: '', status: '' }

export function RetentionReleaseList() {
  const { profile } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({ ...BLANK })
  const [raising, setRaising] = useState<HoldingRow | null>(null)
  const { sort, toggle } = useSort('date_created', 'desc')

  const set = (patch: Partial<typeof BLANK>) => setF((v) => ({ ...v, ...patch }))
  const dirty = Object.values(f).some(Boolean)

  const { data: holding, loading: l1, refresh: r1 } = useQuery<HoldingRow>(
    'v_statutory_retention',
    () => supabase.from('v_statutory_retention').select('*').order('wo_id'),
    [], { cacheable: false, idField: 'wo_id' })

  const { data: releases, loading: l2, refresh: r2 } = useQuery<ReleaseRow>(
    'v_retention_releases',
    () => supabase.from('v_retention_releases').select('*')
      .order('created_at', { ascending: false }).limit(1000),
    [], { cacheable: false })

  const refresh = () => { r1(); r2() }

  const canRaise = MAY_RAISE.includes(profile?.role ?? '')

  const vendors = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of releases) m.set(r.vendor_id, r.vendor_name ?? r.vendor_id)
    for (const h of holding) m.set(h.vendor_id, h.vendor_name ?? h.vendor_id)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [releases, holding])

  const shown = useMemo(() => {
    const t = f.term.trim().toLowerCase()
    return releases.filter((r) =>
      (!f.site || r.site_id === f.site)
      && (!f.vendor || r.vendor_id === f.vendor)
      && (!f.status || r.status === f.status)
      && (!t
        || r.id.toLowerCase().includes(t)
        || r.wo_id.toLowerCase().includes(t)
        || (r.vendor_name ?? '').toLowerCase().includes(t)
        || (r.reason ?? '').toLowerCase().includes(t)))
  }, [releases, f])

  const sorted = useMemo(() => sortRows(shown, sort), [shown, sort])

  const totalHeld = holding.reduce((a, h) => a + n(h.retention_held), 0)
  const totalOut = holding.reduce((a, h) => a + n(h.retention_outstanding), 0)
  const paidBack = holding.reduce((a, h) => a + n(h.retention_paid_out), 0)
  const awaiting = releases.filter((r) => r.status === 'APPROVED' && !r.is_paid)
  const inChain = releases.filter((r) => ['DRAFT', 'SUBMITTED'].includes(r.status))

  // Orders where the money could go back today: the work is done, the defect
  // liability period has run, and there is still something to give.
  const ripe = holding.filter((h) =>
    n(h.retention_outstanding) > 0 && h.work_certified_complete && h.dlp_expired)

  if (l1 || l2) return <Loading label="Reading the retention…" />

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Retention Releases</h1>
        <p className="text-sm text-slate-500">
          Retention is the contractor&rsquo;s money that OVAL holds as security for the
          work. This is where it goes back. Numbered <strong>RETR-</strong>, approved by
          the Project Head and then the CMD.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Held, all time" value={rupees(totalHeld)}
              sub={`across ${holding.length} order${holding.length === 1 ? '' : 's'}`}
              icon={<Lock className="h-4 w-4" />} />
        <Stat label="Still with OVAL" value={rupees(totalOut)}
              tone={totalOut > 0 ? 'warn' : 'good'}
              sub="not yet released"
              icon={<Landmark className="h-4 w-4" />} />
        <Stat label="Gone back" value={rupees(paidBack)}
              tone="good" sub="paid to contractors"
              icon={<HandCoins className="h-4 w-4" />} />
        <Stat label="Awaiting payment" value={awaiting.length}
              tone={awaiting.length ? 'warn' : 'default'}
              sub={awaiting.length
                ? `${rupees(awaiting.reduce((a, r) => a + n(r.amount), 0))} approved, not paid`
                : 'nothing approved and unpaid'}
              icon={<Wallet className="h-4 w-4" />} />
      </div>

      {/* ------------------------------------------------------------------
          MONEY LOCKED BEHIND A DRAFT NOBODY SUBMITTED.

          A draft counts against the ceiling from the moment it is raised —
          that is what stops two people releasing the last rupee on the same
          morning. The cost is that an ABANDONED draft locks the money
          silently, and the only place it would show is a number that is
          smaller than somebody expected.

          So it is said out loud, at the top, with the amount and a way in.
      ------------------------------------------------------------------ */}
      {inChain.length > 0 && (
        <Alert tone="warning"
               title={`${rupees(inChain.reduce((a, r) => a + n(r.amount), 0))} is held behind ${
                 inChain.length === 1 ? 'a release' : 'releases'} still in the chain`}>
          {inChain.map((r, i) => (
            <span key={r.id}>
              {i > 0 && ', '}
              <Link to={`/service/retention-releases/${r.id}`} className="font-medium underline">
                {r.id}
              </Link>
              {' '}({rupees(r.amount)} on {r.wo_id}, {r.status.toLowerCase()})
            </span>
          ))}
          . That much cannot be released again until these are approved or withdrawn — which
          is deliberate, so two people cannot release the same money twice. If one of them is
          not going ahead, open it and withdraw it.
        </Alert>
      )}

      {ripe.length > 0 && (
        <Alert tone="info" title={`${ripe.length} order${ripe.length === 1 ? '' : 's'} could be released today`}>
          The work is certified complete and the defect liability period has run out on{' '}
          {ripe.map((h) => h.wo_id).join(', ')}. That is {rupees(ripe.reduce((a, h) => a + n(h.retention_outstanding), 0))}{' '}
          of somebody else&rsquo;s money still sitting with OVAL.
        </Alert>
      )}

      {/* ------------------------------------------------------------------ */}
      <Card title="Orders holding retention"
            subtitle="What was deducted, what has gone back, and what is still ours to give."
            actions={inChain.length > 0
              ? <span className="pill-amber">{inChain.length} release{inChain.length === 1 ? '' : 's'} in the chain</span>
              : undefined}>
        {holding.length === 0 ? (
          <EmptyState icon={<Lock className="h-8 w-8" />}
                      title="No retention is being held"
                      message="Retention appears here once a running account bill with a retention deduction is approved." />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Work order</th>
                  <th>Contractor</th>
                  <th className="num">Held</th>
                  <th className="num">Released</th>
                  <th className="num">Still ours</th>
                  <th>Defect liability</th>
                  <th>Where it stands</th>
                  {canRaise && <th />}
                </tr>
              </thead>
              <tbody>
                {holding.map((h) => (
                  <tr key={h.wo_id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/work-orders/${h.wo_id}`}
                            className="text-amber-700 hover:underline">{h.wo_id}</Link>
                      <span className="block max-w-[200px] truncate text-[11px] text-slate-400">
                        {h.title}
                      </span>
                    </td>
                    <td className="max-w-[170px] truncate">
                      {h.vendor_name}
                      <span className="block truncate text-[11px] text-slate-400">{h.site_name}</span>
                    </td>
                    <td className="num whitespace-nowrap">{rupees(h.retention_held)}</td>
                    <td className="num whitespace-nowrap text-slate-500">
                      {n(h.retention_released) > 0 ? rupees(h.retention_released) : '—'}
                      {h.releases_awaiting_payment > 0 && (
                        <span className="block text-[11px] text-amber-700">
                          {rupees(h.retention_paid_out)} actually paid
                        </span>
                      )}
                    </td>
                    <td className="num whitespace-nowrap font-semibold">
                      {rupees(h.retention_outstanding)}
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      {h.dlp_ends_on ? (
                        <>
                          {ddmmyyyy(h.dlp_ends_on)}
                          <span className={`block text-[11px] ${h.dlp_expired ? 'text-emerald-700' : 'text-slate-400'}`}>
                            {h.dlp_expired
                              ? 'expired'
                              : `${h.days_to_dlp_end} day${h.days_to_dlp_end === 1 ? '' : 's'} to go`}
                          </span>
                        </>
                      ) : <span className="text-slate-400">no completion date</span>}
                    </td>
                    <td className="max-w-[200px] text-xs text-slate-500">{h.closure_basis}</td>
                    {canRaise && (
                      <td className="whitespace-nowrap text-right">
                        {n(h.retention_outstanding) > 0 ? (
                          <button className="btn-primary btn-sm"
                                  onClick={() => setRaising(h)}>
                            <HandCoins className="h-4 w-4" /> Release
                          </button>
                        ) : (
                          <span className="pill-green">all released</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------------------ */}
      <Card
        title="Releases raised"
        subtitle="Every release, whatever became of it. A rejected one puts the money back."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput value={f.term} onChange={(v) => set({ term: v })}
                         placeholder="RETR, work order, contractor, reason…" />
            <Select className="w-auto" value={f.status}
                    onChange={(e) => set({ status: e.target.value })}
                    placeholder="Any status"
                    options={['DRAFT', 'SUBMITTED', 'APPROVED', 'RETURNED', 'REJECTED']
                      .map((s) => ({ value: s, label: s }))} />
            <Select className="w-auto" value={f.vendor}
                    onChange={(e) => set({ vendor: e.target.value })}
                    placeholder="All contractors"
                    options={vendors.map(([id, name]) => ({ value: id, label: name }))} />
            {dirty && (
              <button className="btn-ghost btn-sm" onClick={() => setF({ ...BLANK })}>
                <FilterX className="h-4 w-4" /> Clear
              </button>
            )}
          </div>
        }
      >
        {sorted.length === 0 ? (
          <EmptyState icon={<HandCoins className="h-8 w-8" />}
                      title={dirty ? 'Nothing matches' : 'No release has been raised yet'}
                      message={dirty
                        ? 'Clear the filters to see everything.'
                        : 'Use the Release button on an order above to start one.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>Release</Th>
                  <Th id="date_created" sort={sort} onSort={toggle}>Raised</Th>
                  <th>Work order</th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Contractor</Th>
                  <Th id="amount" sort={sort} onSort={toggle} align="right">Amount</Th>
                  <th>Why</th>
                  <Th id="status" sort={sort} onSort={toggle}>Status</Th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.id}>
                    <td className="whitespace-nowrap font-medium">
                      <Link to={`/service/retention-releases/${r.id}`}
                            className="text-amber-700 hover:underline">{r.id}</Link>
                    </td>
                    <td className="whitespace-nowrap text-xs">{ddmmyyyy(r.date_created)}</td>
                    <td className="whitespace-nowrap text-xs">
                      <Link to={`/service/work-orders/${r.wo_id}`}
                            className="font-medium text-amber-700 hover:underline">{r.wo_id}</Link>
                    </td>
                    <td className="max-w-[160px] truncate">{r.vendor_name}</td>
                    <td className="num whitespace-nowrap font-semibold">{rupees(r.amount)}</td>
                    <td className="max-w-[240px] truncate text-xs text-slate-500" title={r.reason}>
                      {r.reason}
                    </td>
                    <td><StatusPill status={r.status} /></td>
                    <td className="whitespace-nowrap text-xs">
                      {r.is_paid
                        ? <span className="pill-green">{ddmmyyyy(r.paid_on)}</span>
                        : r.status === 'APPROVED'
                          ? <span className="pill-amber">awaiting payment</span>
                          : <span className="text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RaiseModal order={raising} onClose={() => setRaising(null)}
                  onDone={() => { setRaising(null); refresh(); toast.success('Release raised.') }} />
    </div>
  )
}

/* ========================================================================== */
/*  RAISING ONE                                                               */
/* ========================================================================== */

function RaiseModal({ order, onClose, onDone }: {
  order: HoldingRow | null; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [remarks, setRemarks] = useState('')
  const [busy, setBusy] = useState(false)

  if (!order) return null

  const ceiling = n(order.retention_outstanding)
  const asked = Number(amount) || 0
  const over = asked > ceiling + 0.004
  const ready = asked > 0 && !over && reason.trim().length > 0

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_retention_raise', {
      p_wo_id: order.wo_id,
      p_amount: asked,
      p_reason: reason.trim(),
      p_remarks: remarks.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not raise the release.')); return }
    setAmount(''); setReason(''); setRemarks('')
    onDone()
  }

  return (
    <Modal open onClose={onClose} wide
           title={`Release retention on ${order.wo_id}`}
           subtitle={`${order.vendor_name ?? order.vendor_id} — ${order.title ?? ''}`}
           footer={
             <>
               <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={submit} disabled={!ready || busy}>
                 {busy ? 'Raising…' : 'Raise the release'}
               </button>
             </>
           }>
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Deducted, bill by bill</div>
            <div className="text-lg font-semibold">{rupees(order.retention_held)}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Already released</div>
            <div className="text-lg font-semibold">{rupees(order.retention_released)}</div>
            {order.releases_in_chain > 0 && (
              <div className="text-[11px] text-amber-700">
                includes {order.releases_in_chain} still in the chain
              </div>
            )}
          </div>
          <div className="rounded-lg border border-amber-200 border-l-4 border-l-amber-600 bg-amber-50 p-3">
            <div className="text-[11px] uppercase tracking-wide text-amber-800">You can release up to</div>
            <div className="text-lg font-semibold text-amber-900">{rupees(ceiling)}</div>
          </div>
        </div>

        {/*
          The clause 150 figure, printed and not enforced. The board chose free
          amounts; this is what the standard conditions would have given, so a
          man departing from them is doing it knowingly.
        */}
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
          <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
          <strong>What the contract would have given.</strong> Clause 150 of the standard
          conditions releases retention in two halves — {rupees(order.first_tranche)} when the
          work is certified complete, {rupees(order.second_tranche)} when the defect liability
          period ends{order.dlp_ends_on ? ` on ${ddmmyyyy(order.dlp_ends_on)}` : ''}. OVAL settles
          retention by agreement rather than by schedule, so any amount is allowed here — but the
          reason below is what explains the difference to whoever reads this in five years.
        </div>

        {!order.work_certified_complete && (
          <Alert tone="warning" title="The work is not certified complete">
            {order.closure_basis} Releasing retention now is a decision somebody has to own —
            say so plainly in the reason.
          </Alert>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Amount to release" required
                 error={over ? `That is more than the ${rupees(ceiling)} still held.` : undefined}>
            <Input type="number" min="0" step="0.01" value={amount} invalid={over}
                   onChange={(e) => setAmount(e.target.value)}
                   placeholder={inr(ceiling)} />
          </Field>
          <Field label="Quick fill" hint="Guidance only — edit freely">
            <div className="flex gap-2">
              <button className="btn-ghost btn-sm"
                      onClick={() => setAmount(String(Math.min(n(order.first_tranche), ceiling)))}>
                First half
              </button>
              <button className="btn-ghost btn-sm" onClick={() => setAmount(String(ceiling))}>
                All of it
              </button>
            </div>
          </Field>
        </div>

        <Field label="Why this amount, now" required
               hint="Kept permanently. It is the only record of the decision.">
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Work certified complete 12-08-2026, snag list cleared, no defects called in the period." />
        </Field>

        <Field label="Remarks" hint="Optional.">
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/* ========================================================================== */
/*  ONE RELEASE                                                               */
/* ========================================================================== */

export function RetentionReleaseDetail() {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const { profile } = useAuth()
  const toast = useToast()
  const [paying, setPaying] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: rows, loading, refresh } = useQuery<ReleaseRow>(
    `retr_${id}`,
    () => supabase.from('v_retention_releases').select('*').eq('id', id).limit(1),
    [id], { cacheable: false })

  if (loading) return <Loading label="Reading the release…" />
  const r = rows[0]
  if (!r) {
    return <EmptyState icon={<AlertTriangle className="h-8 w-8" />}
                       title="No such release"
                       message={`Nothing here is called ${id}.`} />
  }

  const canPay = MAY_PAY.includes(profile?.role ?? '')
  const awaitingPayment = r.status === 'APPROVED' && !r.is_paid

  /*
    SUBMITTING IS THE DOCUMENT'S OWN ACT.

    ApprovalChain only draws buttons for something already SUBMITTED — it is
    the chain, not the covering letter. Every other document in this module
    carries its own Submit; reported 20-09-2026 that this one did not, so a
    raised release could not be moved by any means the application offered.

    Who may press it: whoever may raise one. rpc_works_action does not gate
    SUBMIT by role at all — it gates it by status — so the honest client-side
    rule is the same list that got the document made in the first place.
  */
  const isDraft = r.status === 'DRAFT' || r.status === 'RETURNED'
  const canRaise = MAY_RAISE.includes(profile?.role ?? '')
  const iRaisedIt = !!r.created_by && r.created_by === profile?.id
  const canWithdraw = isDraft
    && (iRaisedIt || ['admin', 'accounts_spoc'].includes(profile?.role ?? ''))

  const act = async (fn: () => PromiseLike<{ error: unknown }>, done: string) => {
    setBusy(true)
    const { error } = await fn()
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'That did not go through.')); return }
    setWithdrawing(false); setWhy('')
    refresh()
    toast.success(done)
  }

  const submit = () => act(
    () => supabase.rpc('rpc_works_action',
      { p_doc_type: 'RETR', p_doc_id: r.id, p_action: 'SUBMIT' }),
    'Sent to the Project Head.')

  /*
    THE CONTRACTOR'S COPY — 0166. Retention is his own money, held back bill
    by bill; until now he got it back as a bank credit with nothing tying it
    to the contract it came off.
  */
  const printable = ['APPROVED', 'CANCELLED', 'REJECTED'].includes(r.status)
  const download = async () => {
    setBusy(true)
    try {
      await downloadFromFunction('generate-works-money-pdf',
                                 { retention_id: r.id }, `${r.id}.pdf`)
    } catch (e) {
      toast.error(dbMessage(e, 'Could not produce the PDF.'))
    } finally { setBusy(false) }
  }

  const withdraw = () => act(
    () => supabase.rpc('rpc_retention_withdraw',
      { p_id: r.id, p_reason: why.trim() || null }),
    'Withdrawn. The money is back on the ceiling.')

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button className="btn-ghost btn-sm mb-1" onClick={() => nav('/service/retention-releases')}>
            <ArrowLeft className="h-4 w-4" /> All releases
          </button>
          <h1 className="text-xl font-bold text-slate-900">{r.id}</h1>
          <p className="text-sm text-slate-500">
            {rupees(r.amount)} back to {r.vendor_name} against{' '}
            <Link to={`/service/work-orders/${r.wo_id}`} className="text-amber-700 hover:underline">
              {r.wo_id}
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={r.status} />
          {r.is_paid && <span className="pill-green">paid {ddmmyyyy(r.paid_on)}</span>}
          {awaitingPayment && <span className="pill-amber">awaiting payment</span>}
          {printable && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => void download()}
                    title={r.is_paid
                      ? 'Payment advice for the contractor'
                      : 'Sanction — the payment advice follows when the money goes'}>
              <FileDown className="h-4 w-4" />
              {r.is_paid ? 'Payment advice' : 'Sanction'}
            </button>
          )}
          {isDraft && canWithdraw && (
            <button className="btn-secondary btn-sm" disabled={busy}
                    onClick={() => { setWhy(''); setWithdrawing(true) }}>
              <Trash2 className="h-4 w-4" /> Withdraw
            </button>
          )}
          {isDraft && canRaise && (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => void submit()}>
              <Send className="h-4 w-4" /> {busy ? 'Sending…' : 'Submit for approval'}
            </button>
          )}
        </div>
      </div>

      {/* ------------------------------------------------------------------
          WHAT HAPPENS NEXT, IN ONE SENTENCE.

          A draft release is not harmless while it sits there: it counts
          against what may be released on the order, so an abandoned one
          locks the money. Say so plainly, on the document, where the person
          who abandoned it will see it.
      ------------------------------------------------------------------ */}
      {isDraft && (
        <Alert tone={r.status === 'RETURNED' ? 'warning' : 'info'}
               title={r.status === 'RETURNED'
                 ? 'Sent back to you'
                 : 'This is a draft — nobody has been asked to approve it yet'}>
          {r.status === 'RETURNED'
            ? 'The Project Head returned it. Deal with what he said, then submit it again.'
            : 'Press Submit for approval and it goes to the Project Head, then the CMD.'}
          {' '}The {rupees(r.amount)} is <strong>already held back</strong> from what else can
          be released on {r.wo_id} while this sits here — so if it is not going ahead,
          withdraw it rather than leaving it.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="The money">
            <div className="grid gap-3 sm:grid-cols-4">
              <Figure label="Released" value={rupees(r.amount)} strong />
              <Figure label="Held when raised" value={rupees(r.held_at_raising)} />
              <Figure label="Released before this" value={rupees(r.released_before)} />
              <Figure label="Still held today" value={rupees(r.unreleased_now)}
                      tone={n(r.unreleased_now) > 0 ? 'warn' : 'good'} />
            </div>
            {r.tranche_guide && (
              <p className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
                {/*
                  Frozen at the moment of raising, not recomputed. If somebody
                  approves a variation next month the contract's halves move,
                  and the note on this document has to keep saying what the man
                  who raised it was looking at.
                */}
                <strong>At the time it was raised:</strong> {r.tranche_guide}
              </p>
            )}
          </Card>

          <Card title="Why">
            <p className="whitespace-pre-wrap text-sm text-slate-700">{r.reason}</p>
            {r.remarks && <p className="mt-2 text-xs text-slate-500">{r.remarks}</p>}
            <p className="mt-3 text-xs text-slate-400">
              Raised by {r.raised_by_name ?? 'somebody no longer on the system'} on{' '}
              {ddmmyyyy(r.date_created)}.
            </p>
            {r.return_reason && (
              <Alert tone="warning" title="Returned">{r.return_reason}</Alert>
            )}
            {r.rejection_reason && (
              <Alert tone="error" title="Rejected">{r.rejection_reason}</Alert>
            )}
          </Card>

          <ApprovalChain docType="RETR" docId={r.id} status={r.status}
                         currentStep={r.current_step} createdBy={r.created_by}
                         onChanged={refresh} />
        </div>

        <div className="space-y-4">
          <Card title="The contract">
            <dl className="space-y-2 text-sm">
              <Row label="Work order" value={
                <Link to={`/service/work-orders/${r.wo_id}`} className="text-amber-700 hover:underline">
                  {r.wo_id}
                </Link>} />
              <Row label="Title" value={r.wo_title ?? '—'} />
              <Row label="Site" value={r.site_name ?? r.site_id} />
              <Row label="Contractor" value={r.vendor_name ?? r.vendor_id} />
              <Row label="PAN" value={r.vendor_pan ?? '—'} />
              <Row label="Work completed" value={r.completion_date ? ddmmyyyy(r.completion_date) : '—'} />
              <Row label="Defect liability ends" value={r.dlp_ends_on ? ddmmyyyy(r.dlp_ends_on) : '—'} />
            </dl>
          </Card>

          {/* ---------------------------------------------------------------
              THE MONEY ACTUALLY LEAVING.
              Separate from the approval, deliberately. Approving a release is
              a decision; paying it is a bank transaction on a different day by
              a different person. One button that did both would put a payment
              date on the system for money that had not moved.
          --------------------------------------------------------------- */}
          <Card title="Payment">
            {r.is_paid ? (
              <dl className="space-y-2 text-sm">
                <Row label="Paid on" value={ddmmyyyy(r.paid_on)} />
                <Row label="Mode" value={r.mode ?? '—'} />
                <Row label="Reference" value={r.reference_no ?? '—'} />
                <p className="pt-1 text-xs text-emerald-700">
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                  The money is back with the contractor.
                </p>
              </dl>
            ) : r.status !== 'APPROVED' ? (
              <p className="text-sm text-slate-500">
                <CalendarClock className="mr-1 inline h-4 w-4" />
                Nothing is paid until the release is approved by the Project Head and the CMD.
              </p>
            ) : canPay ? (
              <>
                <p className="mb-3 text-sm text-slate-600">
                  Approved on {ddmmyyyy(r.approval_date)}. Record the payment once the money
                  has actually left the bank.
                </p>
                <button className="btn-primary btn-sm w-full" onClick={() => setPaying(true)}>
                  <Banknote className="h-4 w-4" /> Record the payment
                </button>
              </>
            ) : (
              <p className="text-sm text-slate-500">
                Approved and waiting for Accounts to pay it.
              </p>
            )}
          </Card>
        </div>
      </div>

      <Modal open={withdrawing} onClose={() => setWithdrawing(false)}
             title="Withdraw this release"
             subtitle={`${r.id} — ${rupees(r.amount)}`}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setWithdrawing(false)}
                         disabled={busy}>Keep it</button>
                 <button className="btn-warning" disabled={busy} onClick={() => void withdraw()}>
                   {busy ? 'Withdrawing…' : 'Withdraw it'}
                 </button>
               </>
             }>
        <div className="space-y-3">
          <Alert tone="info" title="Nothing is deleted">
            The release stays on the register as withdrawn, and the {rupees(r.amount)} goes
            straight back onto what may be released on {r.wo_id}. Both the asking and the
            stopping stay on the record.
          </Alert>
          <Field label="Why" hint="Optional, but this is the entry somebody will ask about.">
            <Textarea rows={3} value={why} onChange={(e) => setWhy(e.target.value)}
                      placeholder="Raised against the wrong order." />
          </Field>
        </div>
      </Modal>

      <PayModal release={paying ? r : null} onClose={() => setPaying(false)}
                onDone={() => { setPaying(false); refresh(); toast.success('Payment recorded.') }} />
    </div>
  )
}

function PayModal({ release, onClose, onDone }: {
  release: ReleaseRow | null; onClose: () => void; onDone: () => void
}) {
  const toast = useToast()
  const today = localIso()
  const [paidOn, setPaidOn] = useState(today)
  const [mode, setMode] = useState('NEFT')
  const [ref, setRef] = useState('')
  const [busy, setBusy] = useState(false)

  if (!release) return null

  const submit = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('rpc_retention_record_payment', {
      p_id: release.id,
      p_paid_on: paidOn,
      p_mode: mode,
      p_reference: ref.trim() || null,
    })
    setBusy(false)
    if (error) { toast.error(dbMessage(error, 'Could not record the payment.')); return }
    onDone()
  }

  return (
    <Modal open onClose={onClose}
           title={`Record the payment of ${rupees(release.amount)}`}
           subtitle={`${release.id} — ${release.vendor_name ?? release.vendor_id}`}
           footer={
             <>
               <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
               <button className="btn-primary" onClick={submit} disabled={busy || !mode}>
                 {busy ? 'Recording…' : 'Record it'}
               </button>
             </>
           }>
      <div className="space-y-4">
        <Field label="Paid on" required>
          <Input type="date" value={paidOn} max={today}
                 onChange={(e) => setPaidOn(e.target.value)} />
        </Field>
        <Field label="How it was paid" required>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}
                  options={['NEFT', 'RTGS', 'IMPS', 'CHEQUE', 'UPI', 'CASH']
                    .map((m) => ({ value: m, label: m }))} />
        </Field>
        <Field label="UTR or cheque number"
               hint="Not compulsory, but it is what makes this findable at the bank.">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */

function Figure({ label, value, strong, tone }: {
  label: string; value: string; strong?: boolean; tone?: 'warn' | 'good'
}) {
  return (
    <div className={`rounded-lg border p-3 ${
      tone === 'warn' ? 'border-amber-200 bg-amber-50'
      : tone === 'good' ? 'border-emerald-200 bg-emerald-50'
      : 'border-slate-200 bg-slate-50'}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${strong ? 'text-lg font-bold' : 'text-sm font-semibold'} text-slate-900`}>
        {value}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-right text-sm text-slate-800">{value}</dd>
    </div>
  )
}

export default RetentionReleaseList
